import { useRef, useState, type ChangeEvent } from "react";

import { api, type ImportResult } from "../api";

interface ImportExportProps {
  onImported: () => void | Promise<void>;
  notify: (message: string, kind?: "info" | "success" | "error") => void;
}

export function ImportExport({ onImported, notify }: ImportExportProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);

  async function handleFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const imported = await api.importCsv(file);
      setResult(imported);
      notify(
        `Imported ${imported.total_rows} rows — ${imported.inserted} added, ${imported.updated} updated, ${imported.skipped} skipped`,
        "success"
      );
      await onImported();
    } catch (error) {
      notify(error instanceof Error ? error.message : "Import failed", "error");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <section className="panel">
      <div className="panel-head">
        <h1>Import / Export</h1>
      </div>

      <div className="card stack">
        <h2>Import CSV</h2>
        <p className="muted">
          Upload a MyFastHabit-format CSV. Rows are matched by start time; a re-import is safe and keeps
          the longer duration for any duplicate start.
        </p>
        <input ref={inputRef} type="file" accept=".csv,text/csv" onChange={handleFile} disabled={busy} />
        {busy && <p className="muted">Importing…</p>}
        {result && (
          <div className="import-result">
            <p>
              <strong>{result.total_rows}</strong> rows · {result.inserted} added · {result.updated}{" "}
              updated · {result.skipped} skipped
            </p>
            {result.warnings.length > 0 && (
              <details>
                <summary>{result.warnings.length} warnings</summary>
                <ul>
                  {result.warnings.map((w) => (
                    <li key={w.row_number}>
                      Row {w.row_number}: {w.message}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>

      <div className="card stack">
        <h2>Export</h2>
        <p className="muted">
          CSV is round-trip compatible with MyFastHabit. JSON is a full-fidelity backup of every fast.
        </p>
        <div className="row">
          <a className="button" href={api.exportUrl("csv")}>
            Download CSV
          </a>
          <a className="button" href={api.exportUrl("json")}>
            Download JSON
          </a>
        </div>
      </div>
    </section>
  );
}
